import React, { useState, useEffect, useRef } from 'react';
import { 
  Box, 
  Typography, 
  Paper, 
  Button, 
  TextField,
  Grid,
  Alert,
  CircularProgress,
  Divider,
  IconButton,
  Tooltip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Card,
  CardContent,
  Badge,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions
} from '@mui/material';
import { useAuth } from '../context/AuthContext';
import QRScanner from '../components/scanning/QRScanner';
import { useTheme } from '../context/ThemeContext';
import MyLocationIcon from '@mui/icons-material/MyLocation';
import QrCodeScannerIcon from '@mui/icons-material/QrCodeScanner';
import CloseIcon from '@mui/icons-material/Close';
import BusinessIcon from '@mui/icons-material/Business';
import AgricultureIcon from '@mui/icons-material/Agriculture';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import CloudOffIcon from '@mui/icons-material/CloudOff';
import CloudDoneIcon from '@mui/icons-material/CloudDone';
import MapIcon from '@mui/icons-material/Map';
import { saveTreeData, isOnline, syncData, getSyncStats, setupSyncListeners } from '../services/DataSyncService';

const ScanPage = () => {
  const { hasPermission, user, isAuthenticated } = useAuth();
  const { darkMode } = useTheme();
  const [scanning, setScanning] = useState(false);
  const [treeId, setTreeId] = useState('');
  const [formData, setFormData] = useState({
    maleFlowers: '',
    femaleFlowers: '',
    longitude: '',
    latitude: ''
  });
  const [sexRatio, setSexRatio] = useState(null);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState('');
  const [loading, setLoading] = useState(false);
  const [geoLoading, setGeoLoading] = useState(false);
  const [clients, setClients] = useState([]);
  const [selectedClient, setSelectedClient] = useState('');
  const [loadingClients, setLoadingClients] = useState(true);
  const [syncStats, setSyncStats] = useState({ total: 0, synced: 0, unsynced: 0 });
  const [networkStatus, setNetworkStatus] = useState(navigator.onLine);
  const [showCameraHelp, setShowCameraHelp] = useState(false);
  const [continuousLocationTracking, setContinuousLocationTracking] = useState(true);
  const [lastLocationUpdate, setLastLocationUpdate] = useState(null);
  const watchPositionRef = useRef(null);
  const [showLocationMap, setShowLocationMap] = useState(false);
  const [locationHistory, setLocationHistory] = useState([]);
  const [recentTrees, setRecentTrees] = useState([]);

  useEffect(() => {
    // Load clients
    loadClients();
    
    // Start continuous location tracking if enabled
    if (continuousLocationTracking) {
      startLocationTracking();
    }
    
    // If we have tree ID but no coordinates, try to get current location
    if (treeId && !formData.latitude && !formData.longitude) {
      getCurrentLocation();
    }

    // Get initial sync stats
    updateSyncStats();
    
    // Load recent tree locations
    loadRecentTreeLocations();

    // Setup network status listeners
    const handleOnlineStatus = () => {
      setNetworkStatus(navigator.onLine);
      updateSyncStats();
    };

    window.addEventListener('online', handleOnlineStatus);
    window.addEventListener('offline', handleOnlineStatus);
    
    // Setup auto-sync when coming online
    setupSyncListeners();
    
    return () => {
      window.removeEventListener('online', handleOnlineStatus);
      window.removeEventListener('offline', handleOnlineStatus);
      
      // Clear location watch when component unmounts
      stopLocationTracking();
    };
  }, [treeId, continuousLocationTracking]);

  const loadRecentTreeLocations = () => {
    try {
      const treeRecords = JSON.parse(localStorage.getItem('treeRecords') || '[]');
      
      // Get the 20 most recent tree records with location data
      const recent = treeRecords
        .filter(record => record.latitude && record.longitude)
        .sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp))
        .slice(0, 20);
      
      setRecentTrees(recent);
    } catch (error) {
      console.error("Error loading recent tree locations:", error);
    }
  };

  const updateSyncStats = () => {
    setSyncStats(getSyncStats());
  };

  const handleSyncNow = () => {
    if (!networkStatus) {
      setError('Device is offline. Cannot sync data now.');
      return;
    }
    
    setLoading(true);
    syncData()
      .then(result => {
        setSuccess(result.message);
        updateSyncStats();
      })
      .catch(error => {
        setError(`Sync failed: ${error.message}`);
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const loadClients = async () => {
    setLoadingClients(true);
    try {
      const { clientAPI } = await import('../services/ApiService');
      const clientsData = await clientAPI.getAllClients();
      setClients(clientsData);
    } catch (error) {
      console.error('Error loading clients:', error);
      setError("Failed to load clients. Please try again.");
    } finally {
      setLoadingClients(false);
    }
  };

  const handleClientChange = (event) => {
    setSelectedClient(event.target.value);
  };

  const handleScan = (result) => {
    if (result) {
      setTreeId(result);
      setScanning(false);
      console.log('Scanned Tree ID:', result);
    }
  };

  const handleError = (error) => {
    console.error('QR scanner error:', error);
    setError(typeof error === 'string' ? error : 'Camera error: ' + (error.message || 'Could not access camera'));
    setScanning(false);
  };

  const startScanning = () => {
    setScanning(true);
    setError(null);
    setSexRatio(null);
    setSuccess('');
  };

  const stopScanning = () => {
    setScanning(false);
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const getCurrentLocation = () => {
    if (navigator.geolocation) {
      setGeoLoading(true);
      navigator.geolocation.getCurrentPosition(
        (position) => {
          setFormData({
            ...formData,
            latitude: position.coords.latitude.toFixed(6),
            longitude: position.coords.longitude.toFixed(6)
          });
          setGeoLoading(false);
        },
        (error) => {
          console.error("Error getting location:", error);
          setError("Could not retrieve location. Please enter manually.");
          setGeoLoading(false);
        },
        { enableHighAccuracy: true }
      );
    } else {
      setError("Geolocation is not supported by this browser.");
    }
  };

  const calculateSexRatio = () => {
    setError(null);
    
    const female = parseInt(formData.femaleFlowers);
    const male = parseInt(formData.maleFlowers);
    
    if (isNaN(female) || isNaN(male)) {
      setError('Please enter valid numbers for flower counts');
      return;
    }
    
    if (female + male === 0) {
      setError('Total flower count cannot be zero');
      return;
    }
    
    const ratio = female / (female + male);
    setSexRatio(ratio.toFixed(3));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setError(null);
    setSuccess('');
    setLoading(true);
    
    if (!selectedClient) {
      setError('Please select a client');
      setLoading(false);
      return;
    }
    
    // Calculate sex ratio
    const female = parseInt(formData.femaleFlowers);
    const male = parseInt(formData.maleFlowers);
    
    if (isNaN(female) || isNaN(male)) {
      setError('Please enter valid numbers for flower counts');
      setLoading(false);
      return;
    }
    
    if (female + male === 0) {
      setError('Total flower count cannot be zero');
      setLoading(false);
      return;
    }
    
    if (!formData.latitude || !formData.longitude) {
      setError('Please provide location information');
      setLoading(false);
      return;
    }
    
    const ratio = female / (female + male);
    const formattedRatio = ratio.toFixed(3);
    
    // Find the selected client details
    const client = clients.find(c => c.id === selectedClient);
    
    // Create tree data object
    const treeData = {
      treeId,
      maleFlowers: male,
      femaleFlowers: female,
      sexRatio: formattedRatio,
      latitude: formData.latitude,
      longitude: formData.longitude,
      timestamp: new Date().toISOString(),
      clientId: selectedClient,
      clientName: client ? client.name : 'Unknown',
      recordedBy: user ? user.username : 'Anonymous worker'
    };
    
    // Save data using DataSyncService
    saveTreeData(treeData)
      .then(result => {
        setSuccess(result.message);
        setSexRatio(formattedRatio);
        updateSyncStats();
        
        // Reset form after successful save
        resetForm();
      })
      .catch(error => {
        setError(error.message || 'Failed to save tree data');
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const resetForm = () => {
    setTreeId('');
    setFormData({
      maleFlowers: '',
      femaleFlowers: '',
      longitude: '',
      latitude: ''
    });
    setSexRatio(null);
    setError(null);
    setSuccess('');
  };

  const getSelectedClientDetails = () => {
    if (!selectedClient) return null;
    return clients.find(client => client.id === selectedClient);
  };

  const clientDetails = getSelectedClientDetails();

  const startLocationTracking = () => {
    if (!navigator.geolocation) {
      setError("Geolocation is not supported by this browser.");
      return;
    }
    
    try {
      // Clear any existing watch
      stopLocationTracking();
      
      // Start watching position with high accuracy
      const watchId = navigator.geolocation.watchPosition(
        (position) => {
          const { latitude, longitude, accuracy } = position.coords;
          
          // Update form data with new coordinates
          setFormData(prev => ({
            ...prev,
            latitude: latitude.toFixed(6),
            longitude: longitude.toFixed(6)
          }));
          
          // Create timestamp and update last location info
          const timestamp = new Date();
          setLastLocationUpdate({
            timestamp,
            accuracy: accuracy.toFixed(1),
            formattedTime: timestamp.toLocaleTimeString()
          });
          
          // Add to location history if position changed significantly
          if (locationHistory.length === 0 || 
              isSignificantMove(latitude, longitude, locationHistory[locationHistory.length-1])) {
            setLocationHistory(prev => [
              ...prev, 
              { 
                latitude, 
                longitude, 
                accuracy, 
                timestamp: timestamp.toISOString(),
                formattedTime: timestamp.toLocaleTimeString()
              }
            ].slice(-50)); // Keep last 50 locations
          }
          
          setGeoLoading(false);
        },
        (error) => {
          console.error("Error tracking location:", error);
          
          // Try single location update on error
          getCurrentLocation();
          
          // Disable continuous tracking on persistent errors
          if (error.code === error.PERMISSION_DENIED) {
            setContinuousLocationTracking(false);
            setError("Location permission denied. Please enable location access.");
          }
        },
        { 
          enableHighAccuracy: true,
          maximumAge: 10000,     // Accept cached positions up to 10 seconds old
          timeout: 20000         // Wait up to 20 seconds for a position
        }
      );
      
      watchPositionRef.current = watchId;
      setGeoLoading(true);
    } catch (error) {
      console.error("Error setting up location tracking:", error);
      setError("Failed to start location tracking. Please try manual location entry.");
    }
  };

  // Determine if the position change is significant enough to record
  // (more than 5 meters from the last recorded position)
  const isSignificantMove = (lat, lon, lastLocation) => {
    if (!lastLocation) return true;
    
    // Quick distance calculation between two points (Haversine formula)
    const R = 6371e3; // Earth radius in meters
    const φ1 = lat * Math.PI/180;
    const φ2 = lastLocation.latitude * Math.PI/180;
    const Δφ = (lastLocation.latitude - lat) * Math.PI/180;
    const Δλ = (lastLocation.longitude - lon) * Math.PI/180;

    const a = Math.sin(Δφ/2) * Math.sin(Δφ/2) +
              Math.cos(φ1) * Math.cos(φ2) *
              Math.sin(Δλ/2) * Math.sin(Δλ/2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a));
    const distance = R * c;
    
    return distance > 5; // Consider significant if moved more than 5 meters
  };

  const stopLocationTracking = () => {
    if (watchPositionRef.current !== null && navigator.geolocation) {
      navigator.geolocation.clearWatch(watchPositionRef.current);
      watchPositionRef.current = null;
    }
  };

  const toggleLocationTracking = () => {
    if (continuousLocationTracking) {
      stopLocationTracking();
    } else {
      startLocationTracking();
    }
    setContinuousLocationTracking(!continuousLocationTracking);
  };

  if (!hasPermission('can_scan')) {
    return (
      <Box sx={{ p: 2, textAlign: 'center' }}>
        <Typography variant="h6" color="error" gutterBottom>
          Access Denied
        </Typography>
        <Typography variant="body1" paragraph>
          You don't have permission to scan trees.
        </Typography>
        <Typography variant="body2" color="textSecondary">
          Please contact an administrator if you need access to this feature.
        </Typography>
      </Box>
    );
  }

  if (clients.length === 0 && !loadingClients) {
    return (
      <Box sx={{ p: 2, textAlign: 'center' }}>
        <Typography variant="h6" color="warning.main" gutterBottom>
          No Clients Available
        </Typography>
        <Typography paragraph>
          There are no clients in the system. Please ask an administrator to add clients before scanning trees.
        </Typography>
        <Button variant="contained" color="primary" onClick={loadClients}>
          Refresh
        </Button>
      </Box>
    );
  }

  return (
    <>
      <Box sx={{ p: 2, mb: 8 }}>
        <Typography variant="h5" gutterBottom>
          Tree QR Scanner
        </Typography>

        {/* Network Status Indicator */}
        <Paper elevation={2} sx={{ p: 2, mb: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              {networkStatus ? (
                <CloudDoneIcon color="success" sx={{ mr: 1 }} />
              ) : (
                <CloudOffIcon color="error" sx={{ mr: 1 }} />
              )}
              <Typography variant="body1">
                Status: {networkStatus ? 'Online' : 'Offline'}
              </Typography>
            </Box>
            
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <Typography variant="body2" sx={{ mr: 2 }}>
                {syncStats.synced}/{syncStats.total} records synced
              </Typography>
              
              {syncStats.unsynced > 0 && (
                <Badge badgeContent={syncStats.unsynced} color="error">
                  <Button 
                    variant="outlined" 
                    startIcon={<CloudUploadIcon />} 
                    size="small"
                    onClick={handleSyncNow}
                    disabled={!networkStatus || loading}
                  >
                    Sync Now
                  </Button>
                </Badge>
              )}
            </Box>
          </Box>
        </Paper>
        
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
            <Box mt={1}>
              <Typography variant="body2" color="text.secondary">
                Having camera issues? <Button size="small" onClick={() => setShowCameraHelp(prev => !prev)}>
                  {showCameraHelp ? 'Hide help' : 'Show help'}
                </Button>
              </Typography>
              
              {showCameraHelp && (
                <Box mt={2} p={2} bgcolor="rgba(0,0,0,0.03)" borderRadius={1}>
                  <Typography variant="subtitle2" gutterBottom>Troubleshooting steps:</Typography>
                  <ul>
                    <li>Make sure you're using a secure HTTPS connection</li>
                    <li>Grant camera permission when prompted</li>
                    <li>Try using Chrome, Safari or Firefox on a recent device</li>
                    <li>Close other apps that might be using your camera</li>
                    <li>If on iOS, avoid using in-app browsers (open in Safari instead)</li>
                    <li>Restart your browser or device if issues persist</li>
                  </ul>
                </Box>
              )}
            </Box>
          </Alert>
        )}
        
        {success && (
          <Alert severity="success" sx={{ mb: 2 }}>
            {success}
          </Alert>
        )}

        {/* Alternative manual input in case scanner doesn't work */}
        <Paper elevation={1} sx={{ p: 2, mb: 3 }}>
          <Typography variant="subtitle1" gutterBottom>
            Alternative: Enter Tree ID Manually
          </Typography>
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
            <TextField
              label="Tree ID"
              variant="outlined"
              size="small"
              value={treeId}
              onChange={(e) => setTreeId(e.target.value)}
              fullWidth
            />
            <Button 
              variant="contained" 
              disabled={!treeId.trim()} 
              onClick={() => setScanning(false)}
            >
              Use This ID
            </Button>
          </Box>
        </Paper>

        {/* Client Selection */}
        <Paper elevation={2} sx={{ p: 2, mb: 2 }}>
          <Typography variant="h6" gutterBottom>
            Select Client
          </Typography>
          
          {loadingClients ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
              <CircularProgress size={30} />
            </Box>
          ) : (
            <FormControl fullWidth margin="normal">
              <InputLabel id="client-select-label">Client/Plantation *</InputLabel>
              <Select
                labelId="client-select-label"
                value={selectedClient}
                onChange={handleClientChange}
                label="Client/Plantation *"
                required
                displayEmpty
                renderValue={(selected) => {
                  if (!selected) {
                    return <em>Select a client</em>;
                  }
                  const client = clients.find(c => c.id === selected);
                  return (
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <BusinessIcon sx={{ mr: 1, fontSize: 20 }} />
                      {client ? client.name : ''}
                    </Box>
                  );
                }}
              >
                {clients.map((client) => (
                  <MenuItem key={client.id} value={client.id}>
                    <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                      <BusinessIcon sx={{ mr: 1, color: 'primary.main' }} />
                      <Box sx={{ flexGrow: 1 }}>
                        <Typography variant="body1">{client.name}</Typography>
                        {client.companyName && (
                          <Typography variant="body2" color="text.secondary" fontSize="0.75rem">
                            {client.companyName}
                          </Typography>
                        )}
                      </Box>
                      <Chip 
                        size="small" 
                        label={client.plantationType}
                        icon={<AgricultureIcon />}
                        color="primary"
                        variant="outlined"
                      />
                    </Box>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}
          
          {clientDetails && (
            <Card variant="outlined" sx={{ mt: 2, backgroundColor: theme => theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.02)' }}>
              <CardContent>
                <Typography variant="subtitle2" color="primary" gutterBottom>
                  Selected Plantation Details
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="body2" gutterBottom>
                      <strong>Type:</strong> {clientDetails.plantationType}
                    </Typography>
                    <Typography variant="body2" gutterBottom>
                      <strong>Age:</strong> {clientDetails.plantationAge} years
                    </Typography>
                    <Typography variant="body2" gutterBottom>
                      <strong>Soil:</strong> {clientDetails.soilProfile || 'N/A'}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="body2" gutterBottom>
                      <strong>Fertilizer:</strong> {clientDetails.fertilizerType || 'N/A'}
                    </Typography>
                    <Typography variant="body2" gutterBottom>
                      <strong>Fertilizer Use:</strong> {clientDetails.fertilizerYears || 'N/A'} years
                    </Typography>
                    <Typography variant="body2" gutterBottom>
                      <strong>Location:</strong> {clientDetails.location || 'N/A'}
                    </Typography>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          )}
        </Paper>

        <Grid container spacing={2}>
          {/* Left column for scanning */}
          <Grid item xs={12} md={6}>
            <Paper elevation={2} sx={{ p: 2, mb: 2, height: '100%' }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="h6" gutterBottom>
                  {treeId ? `Tree ID: ${treeId}` : 'Scan QR Code'}
                </Typography>
                
                {treeId && (
                  <IconButton 
                    size="small" 
                    color="primary" 
                    onClick={() => setTreeId('')}
                    aria-label="Reset scanned tree"
                  >
                    <CloseIcon />
                  </IconButton>
                )}
              </Box>
              
              {!treeId && !scanning && (
                <Button
                  variant="contained"
                  color="primary"
                  onClick={startScanning}
                  fullWidth
                  startIcon={<QrCodeScannerIcon />}
                  disabled={!selectedClient}
                >
                  Start Scanning
                </Button>
              )}
              
              {!treeId && scanning && (
                <>
                  <Box sx={{ position: 'relative', width: '100%', height: '400px', mb: 2 }}>
                    <QRScanner
                      onResult={handleScan}
                      onError={handleError}
                    />
                  </Box>
                  <Button
                    variant="outlined"
                    color="secondary"
                    onClick={stopScanning}
                    fullWidth
                  >
                    Cancel Scanning
                  </Button>
                </>
              )}
              
              {treeId && (
                <Box>
                  <Typography variant="body1" sx={{ mb: 2 }}>
                    Scan another QR code or continue with data entry for this tree.
                  </Typography>
                  <Button
                    variant="outlined"
                    color="primary"
                    onClick={startScanning}
                    fullWidth
                    startIcon={<QrCodeScannerIcon />}
                    sx={{ mb: 2 }}
                  >
                    Scan Another Tree
                  </Button>
                </Box>
              )}
            </Paper>
          </Grid>
          
          {/* Right column for data entry */}
          <Grid item xs={12} md={6}>
            <Paper elevation={2} sx={{ p: 2, mb: 2 }}>
              <Typography variant="h6" gutterBottom>
                Tree Data Entry
              </Typography>
              
              <form onSubmit={handleSubmit}>
                {/* Location Information */}
                <Box sx={{ mb: 3 }}>
                  <Typography variant="subtitle1" gutterBottom display="flex" alignItems="center" justifyContent="space-between">
                    <span>Location Information</span>
                    <Button 
                      size="small" 
                      variant={continuousLocationTracking ? "contained" : "outlined"} 
                      color={continuousLocationTracking ? "success" : "primary"}
                      onClick={toggleLocationTracking}
                      startIcon={continuousLocationTracking ? <MyLocationIcon /> : <MyLocationIcon />}
                    >
                      {continuousLocationTracking ? "Auto-tracking" : "Manual"}
                    </Button>
                  </Typography>
                  
                  {lastLocationUpdate && (
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                      Last update: {lastLocationUpdate.formattedTime} 
                      {lastLocationUpdate.accuracy && ` • Accuracy: ±${lastLocationUpdate.accuracy}m`}
                    </Typography>
                  )}
                  
                  <Grid container spacing={2}>
                    <Grid item xs={5}>
                      <TextField
                        fullWidth
                        label="Latitude"
                        name="latitude"
                        value={formData.latitude}
                        onChange={handleChange}
                        required
                        margin="normal"
                        size="small"
                        disabled={geoLoading || continuousLocationTracking}
                        InputProps={{
                          endAdornment: geoLoading && <CircularProgress size={20} />
                        }}
                      />
                    </Grid>
                    <Grid item xs={5}>
                      <TextField
                        fullWidth
                        label="Longitude"
                        name="longitude"
                        value={formData.longitude}
                        onChange={handleChange}
                        required
                        margin="normal"
                        size="small"
                        disabled={geoLoading || continuousLocationTracking}
                        InputProps={{
                          endAdornment: geoLoading && <CircularProgress size={20} />
                        }}
                      />
                    </Grid>
                    <Grid item xs={2} sx={{ display: 'flex', alignItems: 'center', mt: 2 }}>
                      <Tooltip title={continuousLocationTracking ? "Auto-tracking enabled" : "Get current location"}>
                        <span>
                          <IconButton 
                            color="primary" 
                            onClick={getCurrentLocation}
                            disabled={geoLoading || continuousLocationTracking}
                          >
                            <MyLocationIcon />
                          </IconButton>
                        </span>
                      </Tooltip>
                    </Grid>
                  </Grid>
                  
                  {formData.latitude && formData.longitude && (
                    <Button
                      size="small"
                      variant="text"
                      color="primary"
                      sx={{ mt: 1 }}
                      href={`https://www.google.com/maps/search/?api=1&query=${formData.latitude},${formData.longitude}`}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      View on Map
                    </Button>
                  )}
                </Box>
                
                <Divider sx={{ my: 2 }} />
                
                {/* Flower Counting */}
                <Box>
                  <Typography variant="subtitle1" gutterBottom>
                    Flower Counting
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={6}>
                      <TextField
                        fullWidth
                        label="Male Flowers"
                        name="maleFlowers"
                        type="number"
                        value={formData.maleFlowers}
                        onChange={handleChange}
                        required
                        margin="normal"
                        size="small"
                        InputProps={{
                          inputProps: { min: 0 }
                        }}
                      />
                    </Grid>
                    <Grid item xs={6}>
                      <TextField
                        fullWidth
                        label="Female Flowers"
                        name="femaleFlowers"
                        type="number"
                        value={formData.femaleFlowers}
                        onChange={handleChange}
                        required
                        margin="normal"
                        size="small"
                        InputProps={{
                          inputProps: { min: 0 }
                        }}
                      />
                    </Grid>
                  </Grid>
                  
                  <Box sx={{ mt: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Button 
                      variant="outlined" 
                      color="primary" 
                      onClick={calculateSexRatio}
                      disabled={!formData.maleFlowers || !formData.femaleFlowers}
                    >
                      Calculate Ratio
                    </Button>
                    
                    {sexRatio !== null && (
                      <Typography 
                        variant="h6" 
                        sx={{ 
                          color: darkMode ? '#4CAF50' : '#2E7D32',
                          fontWeight: 'bold'
                        }}
                      >
                        Sex Ratio: {sexRatio}
                      </Typography>
                    )}
                  </Box>
                </Box>
                
                <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end' }}>
                  <Button 
                    type="submit" 
                    variant="contained" 
                    color="primary"
                    disabled={loading || !selectedClient || !treeId || !formData.maleFlowers || !formData.femaleFlowers || !formData.latitude || !formData.longitude}
                  >
                    {loading ? <CircularProgress size={24} /> : 'Save Data'}
                  </Button>
                </Box>
              </form>
            </Paper>
          </Grid>
        </Grid>
      </Box>
      
      {/* Location Map Dialog */}
      <Dialog
        open={showLocationMap}
        onClose={() => setShowLocationMap(false)}
        fullWidth
        maxWidth="md"
      >
        <DialogTitle>
          Location Tracking Map
          <IconButton
            aria-label="close"
            onClick={() => setShowLocationMap(false)}
            sx={{ position: 'absolute', right: 8, top: 8 }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          <Box sx={{ height: 500, width: '100%', position: 'relative', overflow: 'hidden' }}>
            {/* Simple map visualization of recent tree locations */}
            <Box 
              sx={{ 
                height: '100%', 
                width: '100%', 
                position: 'relative',
                bgcolor: 'rgba(0,0,0,0.03)',
                borderRadius: 1,
                border: '1px solid rgba(0,0,0,0.1)',
                p: 2
              }}
            >
              {/* Legend */}
              <Box sx={{ position: 'absolute', top: 10, right: 10, zIndex: 10, bgcolor: 'background.paper', p: 1, borderRadius: 1, boxShadow: 1 }}>
                <Typography variant="subtitle2" gutterBottom>Legend</Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
                  <Box sx={{ width: 12, height: 12, borderRadius: '50%', bgcolor: 'primary.main', mr: 1 }} />
                  <Typography variant="caption">Current Location</Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
                  <Box sx={{ width: 12, height: 12, borderRadius: '50%', bgcolor: 'success.main', mr: 1 }} />
                  <Typography variant="caption">Recent Trees</Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <Box sx={{ width: 12, height: 12, borderRadius: '50%', bgcolor: 'secondary.light', mr: 1 }} />
                  <Typography variant="caption">Location History</Typography>
                </Box>
              </Box>
              
              {/* Map draw area */}
              <Box sx={{ height: '100%', width: '100%', position: 'relative' }}>
                {locationHistory.length > 0 && formData.latitude && formData.longitude ? (
                  <LocationMapPlot 
                    currentLocation={{ latitude: parseFloat(formData.latitude), longitude: parseFloat(formData.longitude) }}
                    locationHistory={locationHistory}
                    recentTrees={recentTrees}
                  />
                ) : (
                  <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
                    <Typography>No location data available. Start moving to collect location data.</Typography>
                  </Box>
                )}
              </Box>
            </Box>
          </Box>
          
          <Box sx={{ mt: 2 }}>
            <Typography variant="body2" color="text.secondary">
              This map shows your current location (blue), location history (purple), and recently scanned trees (green).
              The tracking system records your movement to help visualize the path between trees.
            </Typography>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowLocationMap(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

// Simple component to plot locations on a relative scale map
const LocationMapPlot = ({ currentLocation, locationHistory, recentTrees }) => {
  const [bounds, setBounds] = useState({ minLat: 0, maxLat: 0, minLng: 0, maxLng: 0 });
  
  useEffect(() => {
    // Calculate map bounds from all points
    const points = [
      currentLocation,
      ...locationHistory,
      ...recentTrees.map(tree => ({ latitude: parseFloat(tree.latitude), longitude: parseFloat(tree.longitude) }))
    ];
    
    if (points.length > 0) {
      const lats = points.map(p => p.latitude);
      const lngs = points.map(p => p.longitude);
      
      setBounds({
        minLat: Math.min(...lats),
        maxLat: Math.max(...lats),
        minLng: Math.min(...lngs),
        maxLng: Math.max(...lngs)
      });
    }
  }, [currentLocation, locationHistory, recentTrees]);
  
  // Convert geo coordinates to pixel positions
  const coordToPixel = (lat, lng) => {
    // Add padding to bounds
    const padFactor = 0.2;
    const latRange = (bounds.maxLat - bounds.minLat) * (1 + padFactor);
    const lngRange = (bounds.maxLng - bounds.minLng) * (1 + padFactor);
    
    const minLat = bounds.minLat - (latRange * padFactor / 2);
    const minLng = bounds.minLng - (lngRange * padFactor / 2);
    
    // Normalize to 0-1 range
    const x = (lng - minLng) / lngRange;
    const y = 1 - (lat - minLat) / latRange; // Invert Y since 0,0 is top-left
    
    return { x: x * 100 + '%', y: y * 100 + '%' };
  };
  
  return (
    <Box sx={{ position: 'relative', height: '100%', width: '100%' }}>
      {/* Draw location history points */}
      {locationHistory.map((loc, index) => {
        const { x, y } = coordToPixel(loc.latitude, loc.longitude);
        return (
          <Box
            key={`history-${index}`}
            sx={{
              position: 'absolute',
              width: 6,
              height: 6,
              borderRadius: '50%',
              bgcolor: 'secondary.light',
              transform: 'translate(-50%, -50%)',
              left: x,
              top: y,
              opacity: 0.7
            }}
          />
        );
      })}
      
      {/* Draw recent trees */}
      {recentTrees.map((tree, index) => {
        const { x, y } = coordToPixel(parseFloat(tree.latitude), parseFloat(tree.longitude));
        return (
          <Tooltip key={`tree-${index}`} title={`Tree: ${tree.treeId}`}>
            <Box
              sx={{
                position: 'absolute',
                width: 10,
                height: 10,
                borderRadius: '50%',
                bgcolor: 'success.main',
                transform: 'translate(-50%, -50%)',
                left: x,
                top: y,
                zIndex: 5
              }}
            />
          </Tooltip>
        );
      })}
      
      {/* Draw current location */}
      {currentLocation && (
        <Box
          sx={{
            position: 'absolute',
            width: 12,
            height: 12,
            borderRadius: '50%',
            bgcolor: 'primary.main',
            border: '2px solid white',
            boxShadow: 2,
            transform: 'translate(-50%, -50%)',
            left: coordToPixel(currentLocation.latitude, currentLocation.longitude).x,
            top: coordToPixel(currentLocation.latitude, currentLocation.longitude).y,
            zIndex: 10
          }}
        />
      )}
    </Box>
  );
};

export default ScanPage; 